import { Box, Card, CardContent, Typography, Chip, Grid } from "@mui/material";
import React from "react";

import { useCelleraTheme } from "../../../theme/useCelleraTheme";

export enum SubscriptionStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  PAST_DUE = "past_due",
  CANCELED = "canceled",
  INCOMPLETE = "incomplete",
  INCOMPLETE_EXPIRED = "incomplete_expired",
  PAUSED = "paused",
  TRIALING = "trialing",
  UNPAID = "unpaid",
}

export interface SubscriptionData {
  planName: string;
  price: string;
  renewalDate: string;
  status: SubscriptionStatus;
}

interface SubscriptionSummaryProps {
  subscription: SubscriptionData | null;
  loading?: boolean;
}

const SubscriptionSummary: React.FC<SubscriptionSummaryProps> = ({
  subscription,
  loading = false,
}) => {
  const { colors } = useCelleraTheme();

  const getStatusColor = (
    status: SubscriptionStatus,
  ): "success" | "default" | "error" | "warning" => {
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return "success";
      case SubscriptionStatus.INACTIVE:
        return "default";
      case SubscriptionStatus.CANCELED:
        return "error";
      case SubscriptionStatus.PAST_DUE:
        return "warning";
      default:
        return "default";
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ mr: 2 }}>
              Current Subscription Plan
            </Typography>
            <Box
              sx={{
                width: 80,
                height: 24,
                backgroundColor: "#f0f0f0",
                borderRadius: 1,
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  width: "100%",
                  height: 40,
                  backgroundColor: "#f0f0f0",
                  borderRadius: 1,
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  width: "100%",
                  height: 40,
                  backgroundColor: "#f0f0f0",
                  borderRadius: 1,
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  width: "100%",
                  height: 40,
                  backgroundColor: "#f0f0f0",
                  borderRadius: 1,
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }

  if (!subscription) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Current Subscription Plan
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No subscription information available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" sx={{ mr: 2 }}>
            Current Subscription Plan
          </Typography>
          <Chip
            label={subscription.status?.toUpperCase()}
            color={getStatusColor(subscription.status)}
            size="small"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                Plan Name
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: colors.primary.main,
                  fontWeight: 600,
                }}
              >
                {subscription.planName}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                Pricing
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: colors.primary.main,
                  fontWeight: 600,
                }}
              >
                {subscription.price}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                Renewal Date
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: colors.primary.main,
                  fontWeight: 600,
                }}
              >
                {formatDate(subscription.renewalDate)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SubscriptionSummary;
